import { SnPoint, SnPointLike } from '@lib/geo/helpers';
import { SnBadgeConfig } from '@lib/ui/badge';

import { GoogleMapMarkerIconComponent } from '../../components';
import { GoogleMapComponentMarker } from './component-marker';
import { IIconMarkerEvents } from './icon-marker-events';
import {
  GoogleMapIconMarkerIcon,
  GoogleMapIconMarkerOptions,
  IGoogleMapIconMarkerOptions,
} from './icon-marker-options';
import { GoogleMapMarkerIconLabel } from './marker-options';

const DEFAULT_OPTIONS: IGoogleMapIconMarkerOptions = {
  scale: 1,
  color: '#000000',
  anchor: [0, 0],
};

export class GoogleMapIconMarker extends GoogleMapComponentMarker<
  GoogleMapMarkerIconComponent,
  IIconMarkerEvents
> {
  private _icon: GoogleMapIconMarkerIcon;
  private _iconLabel: GoogleMapMarkerIconLabel;
  private _badge: SnBadgeConfig;
  private _scale: number;
  private _color: string;
  private _anchor: SnPoint;
  private _rotation: number;

  constructor(options?: GoogleMapIconMarkerOptions) {
    super(GoogleMapMarkerIconComponent, options);

    options = { ...DEFAULT_OPTIONS, ...options };
    this._scale = options.scale;
    this._color = options.color;
    this._badge = options.badge;
    this._anchor = SnPoint.convert(options.anchor);

    if ('name' in options) {
      this._icon = options.name;
    } else if ('fontIcon' in options) {
      this._icon = {
        fontIcon: options.fontIcon,
        fontSet: options.fontSet,
        size: options.size,
      };
    } else if ('fontIconUnicode' in options) {
      this._icon = {
        fontIconUnicode: options.fontIconUnicode,
        fontSet: options.fontSet,
      };
    }
  }

  public getIcon(): GoogleMapIconMarkerIcon {
    return this._icon;
  }

  public setIcon(icon: GoogleMapIconMarkerIcon): this {
    this._icon = icon;
    this._updateIcon();

    this.fire('iconChange');

    return this;
  }

  public setIconLabel(label: GoogleMapMarkerIconLabel): this {
    this._iconLabel = label;
    this._updateIconLabel();
    return this;
  }

  public setBadge(badge: SnBadgeConfig): this {
    this._badge = badge;
    this._updateBadge();

    return this;
  }

  public setColor(color: string): this {
    this._color = color;
    this._updateStyles();

    return this;
  }

  public setScale(scale: number): this {
    this._scale = scale;
    this._updateStyles();

    return this;
  }

  public setRotation(deg: number): this {
    this._rotation = deg;
    this._updateStyles();

    return this;
  }

  public getAnchor(): SnPoint {
    return this._anchor;
  }

  public setAnchor(anchor: SnPointLike): this {
    this._anchor = SnPoint.convert(anchor);
    this._updateStyles();

    return this;
  }

  protected onAdd(): void {
    super.onAdd();

    this._updateIcon();
    this._updateBadge();
    this._updateStyles();
    this._updateIconLabel();
  }

  private _updateIcon(): void {
    const componentInstance = this.getComponentInstance();
    if (componentInstance) {
      componentInstance.setIcon(this._icon);
    }
  }

  private _updateIconLabel(): void {
    const componentInstance = this.getComponentInstance();

    if (componentInstance) {
      componentInstance.setIconLabel(this._iconLabel);
    }
  }

  private _updateBadge(): void {
    const componentInstance = this.getComponentInstance();

    if (componentInstance) {
      componentInstance.setBadge(this._badge);
    }
  }

  private _updateStyles(): void {
    const element = this.getElement();

    element.style.color = this._color;
    element.style.height = '24px';

    const scale = this._scale;
    const deg = this._rotation || 0;

    const transform = ['translate(-50%, -50%)', `scale(${scale})`, `rotate(${deg}deg)`];
    element.style.transform = transform.join(' ');

    const { x, y } = this.getAnchor();
    element.style.marginLeft = `${x * scale}px`;
    element.style.marginTop = `${y * scale}px`;
  }
}
